import { publicHref } from '../lib/publicUrls';
import './EditorialPages.css';
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { TourClosing } from '../components/TourJourney';
import { legacyBlogPosts } from '../data/legacyBlogPosts';
import PhotoSlot from '../components/PhotoSlots';

const legacyCategoryAliases = {
  blog: 'all',
  'child-care': 'Kids & Family',
  'mx4-training': 'MX4 & Small Group',
  'personal-training': 'Personal Training',
  'prenatal-massage': 'Massage & Recovery',
  'reformer-pilates': 'Pilates',
  'rock-climbing': 'Rock Climbing',
  'senior-fitness': 'Senior Fitness',
  'spin-class': 'Cycling',
  'strength-training': 'Strength Training',
  'swim-lessons': 'Aquatics',
  'swimming-lessons': 'Aquatics',
  'water-aerobics': 'Aquatics',
  'yoga-flow': 'Mind & Body',
  'yoga-muscle-fusion': 'Mind & Body',
  'sports-massage': 'Massage & Recovery',
};

const posts = legacyBlogPosts;
const categories = [
  'all',
  ...Array.from(new Set(posts.map((post) => post.category).filter(Boolean))),
];

const formatDate = (value) => {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const BlogPage = () => {
  const { categorySlug } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const alias = Object.prototype.hasOwnProperty.call(legacyCategoryAliases, categorySlug) ? legacyCategoryAliases[categorySlug] : null;
    const normalized = (categorySlug || '').replace(/-/g, ' ').toLowerCase();
    const match = categories.find(category => category.toLowerCase() === normalized);
    setSelectedCategory(alias || match || 'all');
  }, [categorySlug]);

  // Sort blog posts by date (newest first)
  const sortedPosts = [...posts].sort((a, b) => {
    const dateA = new Date(a.created_at || a.date);
    const dateB = new Date(b.created_at || b.date);
    return dateB - dateA;
  });

  const filteredPosts =
    selectedCategory === 'all'
      ? sortedPosts
      : sortedPosts.filter((post) => post.category === selectedCategory);

  // Build the canonical URL for a post: prefer the original WP date URL if present (SEO equity),
  // fallback to /blog/{slug}
  const postUrl = (p) => p.wp_path || `/blog/${p.slug}`;

  const blogListSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Delta Valley Health Club — Fitness Blog',
    description:
      'Expert guides on fitness, Pilates, HIIT, swimming, strength training, and more from Delta Valley Health Club in Brentwood & Discovery Bay, CA.',
    url: 'https://deltavalleyhealthclub.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'Delta Valley Health Club',
      logo: 'https://deltavalleyhealthclub.com/Delta-Valley-Logo.png',
    },
    blogPost: sortedPosts.slice(0, 20).map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.excerpt,
      url: `https://deltavalleyhealthclub.com${p.wp_path || `/blog/${p.slug}`}`,
      datePublished: p.date,
      image: p.image,
    })),
  };

  return (
    <div className="dv-editorial dv-editorial--listing" data-testid="blog-page">
      <SEO
        title="Fitness Blog & Expert Guides"
        description="Expert fitness guides on Pilates, HIIT, swimming, strength training, and more. Tips for members at Delta Valley Health Club in Brentwood & Discovery Bay, CA."
        keywords="fitness blog, Pilates tips, HIIT guide, swimming, water aerobics, strength training, gym Brentwood"
        schema={blogListSchema}
      />
      {/* Hero */}
      <section className="dv-editorial__hero">
        <div className="container-custom dv-editorial__hero-inner">
          <p className="dv-eyebrow mb-4">Hot Topics</p>
          <h1 className="dv-size-heading text-5xl md:text-6xl font-bold mb-6">Fitness Blog</h1>
          <p className="dv-size-lede text-xl text-blue-100 max-w-2xl">
            Expert tips, guides, and insights to help you achieve your fitness goals
          </p>
        </div>
      </section>

      {/* Blog Section */}
      <section className="dv-editorial__section">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="dv-editorial__filters">
            <div className="dv-editorial__filter-layout">
              <span id="blog-categories-label" className="text-sm font-medium text-gray-600">Categories:</span>
              <div className="dv-editorial__filter-options" role="group" aria-labelledby="blog-categories-label">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant="ghost"
                    size="sm"
                    aria-pressed={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ? 'bg-gray-100' : ''}
                  >
                    {category === 'all' ? 'All Posts' : category}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <p className="dv-editorial__result-count" role="status" aria-live="polite">{filteredPosts.length} articles · {selectedCategory === 'all' ? 'All Posts' : selectedCategory}</p>

          {/* Blog Posts Grid */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="blog-empty">No posts found in this category.</div>
          ) : (
          <div className="dv-editorial__listing" data-testid="blog-grid">
            {filteredPosts.map((post) => (
              <Link key={post.slug} to={publicHref(postUrl(post))} data-testid={`blog-card-link-${post.slug}`}>
                <Card className="dv-editorial__story group" data-testid={`blog-card-${post.slug}`}>
                  <div className="dv-editorial__story-image">
                    <PhotoSlot id={`article-${post.slug}`} label={`${post.title} — article photo`} className="w-full h-full" />
                    <div className="dv-editorial__story-category">
                      <Badge className="bg-white text-gray-900">{post.category}</Badge>
                    </div>
                  </div>
                  <CardContent className="dv-editorial__story-copy">
                    <h3 className="dv-size-subheading text-xl font-bold mb-2 group-hover:text-blue-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="dv-size-body text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        {post.read_time || post.readTime}
                      </div>
                      <span>{formatDate(post.date)}</span>
                    </div>
                    <div className="mt-4 flex items-center text-blue-600 font-medium">
                      Read More
                      <ArrowRight className="ml-2" size={16} />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          )}
        </div>
      </section>

      <TourClosing />
    </div>
  );
};

export default BlogPage;
